import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import { PanelCard } from '../Dashboard';
import { Modal } from '../review/reviewUi';
import RiskPanelForm from './RiskPanelForm';
import RiskPanelCard from './RiskPanelCard';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

/**
 * Risk Management home (mockup screen 1): one card per risk file (panel).
 *
 * A panel = a name + one Orcanos Risk filter, plus optional Test Case filters for the
 * verification side. Risks stay in Orcanos (design R3) — this screen reads them, scores them
 * with the project's own FMEA settings, and shows whether each one is controlled.
 */
export default function RiskHome({ canManagePanels, hasTrace = false }) {
  const [panels, setPanels] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Traceability panels whose level 1 is Risk, linked onto this page (v4.20.0). They stay
  // Traceability panels: Full View / Edit open the trace screens, the bin only unlinks.
  const [trace, setTrace] = useState({ linked: [], candidates: [] });
  // A failed load must never read as "no panel starts from Risk" — the picker shows it instead.
  const [traceError, setTraceError] = useState('');
  const [picker, setPicker] = useState(false);
  const [projectNames, setProjectNames] = useState({});
  const navigate = useNavigate();

  const loadTrace = useCallback(async () => {
    if (!hasTrace) return;
    try {
      const t = await riskApi.tracePanels();
      setTrace({ linked: t.linked || [], candidates: t.candidates || [] });
      setTraceError('');
    } catch (e) {
      setTraceError(e.status === 404
        ? 'The server does not know this feature yet — restart the backend and try again.'
        : e.message);
    }
  }, [hasTrace]);

  useEffect(() => { loadTrace(); }, [loadTrace]);

  // Project names for the trace cards' "Project (Version)" line — the same lookup the
  // Traceability dashboard uses. Only fetched once there is a card to label.
  const needNames = trace.linked.length > 0 || picker || (panels?.length || 0) > 0;
  useEffect(() => {
    if (!needNames) return undefined;
    let live = true;
    fetch('/api/config/projects', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!live || !Array.isArray(d.projects)) return;
        const m = {};
        d.projects.forEach((p) => {
          if (p.project_id == null) return;
          const id = String(p.project_id);
          if (!m[id]) m[id] = p.project_name || '';
          if (p.version_id != null) {
            m[`${id}|${p.version_id}`] = p.version_label ? `${p.project_name || id} (${p.version_label})` : (p.project_name || id);
          }
        });
        setProjectNames(m);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [needNames]);

  const projectLabel = (p) => projectNames[`${p.project_id}|${p.version_id}`]
    || projectNames[String(p.project_id)] || String(p.project_id ?? '');

  const linkTrace = async (id) => {
    try {
      await riskApi.linkTracePanel(id);
      setPicker(false);
      loadTrace();
    } catch (e) {
      setError(e.message);
    }
  };

  const unlinkTrace = async (p) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove “${p.name}” from Risk Management? The Traceability panel itself is kept.`)) return;
    try {
      await riskApi.unlinkTracePanel(p.id);
      loadTrace();
    } catch (e) {
      setError(e.message);
    }
  };

  const load = useCallback(async () => {
    try {
      const p = await riskApi.panels();
      setPanels(p.panels || []);
      setError('');
    } catch (e) {
      setError(e.message);
      setPanels((x) => x || []);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (panel) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the panel “${panel.name}”? The risks stay in Orcanos.`)) return;
    try {
      await riskApi.deletePanel(panel.id);
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  // Move a card one place up or down. The list is redrawn at once; the order is shared per
  // account (same column as the dashboard's), so a failed save reloads the server's order.
  const move = async (index, delta) => {
    const to = index + delta;
    if (!panels || to < 0 || to >= panels.length) return;
    const next = [...panels];
    [next[index], next[to]] = [next[to], next[index]];
    setPanels(next);
    try {
      await riskApi.reorderPanels(next.map((x) => x.id));
    } catch (e) {
      setError(e.message);
      load();
    }
  };

  return (
    <div className="drv-page rsk-page">
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Risk Management</h1>
        </div>
        {canManagePanels && (
          <div className="rsk-head-actions">
            {hasTrace && (
              <button type="button" className="drv-btn drv-btn--lg" onClick={() => { setPicker(true); loadTrace(); }}
                      title="Show a Traceability panel whose first level is Risk on this page">
                + Add from Traceability
              </button>
            )}
            <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={() => setForm({})}>
              + New panel
            </button>
          </div>
        )}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      {panels === null && <div className="drv-muted">Loading panels…</div>}
      {panels?.length === 0 && trace.linked.length === 0 && (
        <div className="drv-empty">
          No risk files yet.{' '}
          {canManagePanels
            ? <>Create one with <strong>+ New panel</strong> — pick an Orcanos Risk filter, and optionally the test-case filters that verify the mitigations.</>
            : 'Ask someone who can create panels to add one.'}
        </div>
      )}

      {(panels || []).map((p, i) => (
        <RiskPanelCard
          key={p.id}
          panel={p}
          projectName={projectLabel(p)}
          canManage={canManagePanels}
          onMoveUp={panels.length > 1 && i > 0 ? () => move(i, -1) : null}
          onMoveDown={panels.length > 1 && i < panels.length - 1 ? () => move(i, 1) : null}
          reloadKey={reloadKey}
          onEdit={() => setForm({ id: p.id })}
          onDelete={() => remove(p)}
        />
      ))}

      {trace.linked.length > 0 && (
        <div className="rsk-trace-linked">
          <h2 className="drv-h2">Traceability <span>· panels that start from Risk</span></h2>
          {trace.linked.map((p) => (
            <PanelCard
              key={p.id}
              panel={p}
              projectLabel={projectLabel(p)}
              onView={() => navigate(`/trace/matrix/${p.id}`, { state: { from: '/risk' } })}
              onEdit={() => navigate(`/trace/config/${p.id}`, { state: { from: '/risk' } })}
              onDelete={() => unlinkTrace(p)}
              deleteTitle="Remove from Risk Management"
              canManagePanels={canManagePanels}
            />
          ))}
        </div>
      )}

      {picker && (
        <Modal
          title="Add from Traceability"
          subtitle="Traceability panels whose first level is Risk. The panel stays in Traceability — this only shows it here too."
          onClose={() => setPicker(false)}
          footer={<button type="button" className="drv-btn drv-btn--lg" onClick={() => setPicker(false)}>Close</button>}
        >
          {traceError ? (
            <div className="drv-error" role="alert">Couldn’t load the Traceability panels: {traceError}</div>
          ) : trace.candidates.length === 0 ? (
            <div className="drv-muted">
              {trace.linked.length
                ? 'Every Traceability panel that starts from Risk is already here.'
                : 'No Traceability panel starts from Risk yet. Create one on the Traceability dashboard with Risk as level 1.'}
            </div>
          ) : (
            <ul className="rsk-trace-pick">
              {trace.candidates.map((p) => {
                const levels = Array.isArray(p.levels) ? p.levels : [];
                const names = [p.base_level_name || 'L1', ...levels.map((l, i) => l.level_name || `L${i + 2}`)];
                return (
                  <li key={p.id}>
                    <div>
                      <strong>{p.name}</strong>
                      <span>{projectLabel(p)} · {names.join(' → ')}</span>
                    </div>
                    <button type="button" className="drv-btn drv-btn--primary" onClick={() => linkTrace(p.id)}>Add</button>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}

      {form && (
        <RiskPanelForm
          panelId={form.id}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); load(); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}
